import PropTypes from 'prop-types'
import { Fragment, useState } from 'react'
import { useHistory } from 'react-router-dom'

import Button from 'components/common/Button'
import Text from 'components/common/Text'
import { ScenarioHaltConfirmationModal } from 'components/scenarios/detail/ScenarioActions'
import { getRunStage } from 'components/scenarios/utils'
import useAgent from 'hooks/use-agent'
import theme from 'theme'

import ScenarioRow from './AttackRows/ScenarioRow'

/**
 * Return list of complete scenarios for selected team and time period
 *
 * @param teamId string
 * @param start string
 * @param end string
 * @param options {}
 * @returns {{isLoading: bool, scenarios: function doFetch: []}}
 */
export function useCompletedScenarios(
  teamId,
  start,
  end,
  options = { interval: 10000 }
) {
  const { data, isLoading, doFetch } = useAgent(
    `/scenarios/runs?state=Terminal&teamId=${teamId}&startDate=${start}&endDate=${end}`,
    [],
    options
  )

  return {
    scenarios: data,
    doFetch,
    isLoading
  }
}

export default function CompletedScenarioList({
  completedScenariosAwaitingResults,
  doFetchCompletedRuns,
  closePopover,
  selectableRows,
  ...rest
}) {
  const history = useHistory()
  const onClick = ({ event, scenario }) => {
    event.preventDefault()

    history.push({
      pathname: `/scenarios/detail/${scenario.scenario_id}/runs/${scenario.run_number}`,
      state: {
        scrollToResults: true
      }
    })
    closePopover()
    return false
  }

  const [selectedRun, setSelectedRun] = useState(false) // TODO this should probably default to something else
  return (
    <Fragment>
      {completedScenariosAwaitingResults.map((scenario) => {
        return (
          <ScenarioRow
            key={`${scenario.scenario_id}-${scenario.run_number}`}
            clickableRow
            isScenarioRun
            as="div"
            doFetch={doFetchCompletedRuns}
            extraRowContent={
              <Button
                data-test-input-results
                secondary
                as="button"
                onClick={(event) => {
                  event.preventDefault()
                  setSelectedRun(scenario)
                  return false
                }}
              >
                Input Results
              </Button>
            }
            scenario={scenario}
            selectableRow={selectableRows}
            showHaltButton={false}
            onClick={(event) => {
              if (event.target.tagName !== 'BUTTON') {
                onClick({ event, scenario })
              }
            }}
            {...rest}
          />
        )
      })}
      <ScenarioHaltConfirmationModal
        isCompletedNotification
        isScenarioRun
        confirmButtonType="primary"
        confirmText="Save"
        doFetch={doFetchCompletedRuns}
        haltConfirmationOpen={Boolean(selectedRun)}
        hideOverlay={false}
        scenario={selectedRun}
        scenarioId={selectedRun?.scenario_id}
        selectedRun={selectedRun}
        setDisableButton={() => true}
        setHaltConfirmationOpen={setSelectedRun}
        shouldCloseOnOverlayClick={false}
        showHaltButton={false}
        title={
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column'
            }}
          >
            Scenario Completed
            <Text
              as="small"
              color="medium"
              css={{
                fontSize: theme.fontSizes[1],
                marginTop: theme.space[2]
              }}
            >
              <strong>{selectedRun.name || 'Your scenario'}</strong> has{' '}
              {getRunStage(selectedRun)}
            </Text>
          </div>
        }
      />
    </Fragment>
  )
}

CompletedScenarioList.propTypes = {
  completedScenariosAwaitingResults: PropTypes.array,
  clickableRows: PropTypes.bool,
  selectableRows: PropTypes.bool,
  doFetchCompletedRuns: PropTypes.func,
  closePopover: PropTypes.func
}
